import {
  ConditionalOperatorPrimitive,
  FormFieldSchemaType,
  SchemaPolicyType,
  type FormStepSchema,
} from '@/types';
import countryOptions from '@data-shared/constants/countryOptions';
import { schemaFieldName } from '../schemaFieldName';
import { schemaStepName } from '../schemaStepName';

interface GenerateNewClaimStepWhereWhenFormSchemaParams {
  policyType?: SchemaPolicyType;
}

const { tripDetails, incident } = schemaFieldName;

let stepWhereWhenFormSchema: FormStepSchema = {
  name: schemaStepName.stepWhereWhen,
  title: 'Where and when did this happen',
  titleShort: 'Where and when',
  lastUpdated: Date.now(),
  form: {
    name: `${schemaStepName.stepWhereWhen}Form`,
    fields: [],
  },
};

function generateNewClaimStepWhereWhenFormSchema({
  policyType = SchemaPolicyType.SingleTrip,
}: GenerateNewClaimStepWhereWhenFormSchemaParams = {}): FormStepSchema {
  const { form, ...otherSchemaProps } = stepWhereWhenFormSchema;
  stepWhereWhenFormSchema = {
    ...otherSchemaProps,
    lastUpdated: Date.now(),
    form: {
      name: form.name,
      fields: [
        {
          type: FormFieldSchemaType.Select,
          name: incident.incidentCountry,
          label: 'Enter the country where the incident happened.',
          placeholder: 'Select country',
          options: countryOptions,
        },
        {
          type: FormFieldSchemaType.Text,
          name: incident.incidentCity,
          label: 'Enter the city, town, or province where it happened.',
        },
        {
          type: FormFieldSchemaType.Date,
          name: incident.incidentDate,
          label: 'Enter the date it happened.',
          placeholder: 'dd/mm/yyyy',
          layoutOptions: {
            width: '1/2-inline',
          },
        },
        {
          type: FormFieldSchemaType.Time,
          name: incident.incidentTime,
          label: 'Enter the time it happened.',
          placeholder: 'dd/mm/yyyy',
          layoutOptions: {
            width: '1/2-inline',
          },
        },
        {
          value: policyType,
          name: schemaFieldName.policyType,
          type: FormFieldSchemaType.Select,
          label: 'Select policy type',
          layoutOptions: {
            isVisuallyHidden: true,
          },
          options: [
            {
              label: 'Single Trip',
              value: SchemaPolicyType.SingleTrip,
            },
            {
              label: 'Annual',
              value: SchemaPolicyType.AnnualMultiTrip,
            },
          ],
        },
        {
          type: FormFieldSchemaType.Date,
          name: tripDetails.annualMultiTripDetails.travelStartDate,
          label: 'Enter the trip start date.',
          placeholder: 'DD/MM/YYYY',
          layoutOptions: {
            width: '1/2-inline',
            hasSeparator: true,
          },
          information: {
            title: 'You have an annual multi-trip (AMT) policy.',
            description: (
              <>
                <p>This means you'll need to enter:</p>
                <ul>
                  <li>the start date and end date for the trip involving this incident, and</li>
                  <li>the date you first paid for this trip.</li>
                </ul>
              </>
            ),
            position: 'before',
          },
          isVisibleWhen: [
            {
              field: schemaFieldName.policyType,
              value: SchemaPolicyType.AnnualMultiTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        {
          type: FormFieldSchemaType.Date,
          name: tripDetails.annualMultiTripDetails.travelEndDate,
          label: 'Enter the trip end date.',
          placeholder: 'dd/mm/yyyy',
          layoutOptions: {
            width: '1/2-inline',
          },
          isVisibleWhen: [
            {
              field: schemaFieldName.policyType,
              value: SchemaPolicyType.AnnualMultiTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          isValidWhen: [
            {
              field: tripDetails.annualMultiTripDetails.travelStartDate,
              operator: ConditionalOperatorPrimitive.GreaterThan,
              message: 'Date selected or entered must be after the trip start date',
            },
          ],
        },
        {
          type: FormFieldSchemaType.Date,
          name: tripDetails.annualMultiTripDetails.firstPaymentDate,
          label: 'Enter the date you first made a payment for this trip.',
          placeholder: 'dd/mm/yyyy',
          layoutOptions: {
            width: '1/2-block',
          },
          isVisibleWhen: [
            {
              field: schemaFieldName.policyType,
              value: SchemaPolicyType.AnnualMultiTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
      ],
    },
  };

  return stepWhereWhenFormSchema;
}

function getNewClaimStepWhereWhenFormSchema() {
  if (!stepWhereWhenFormSchema.form.fields.length) {
    // Generate a new schema with all fields, if its empty!
    stepWhereWhenFormSchema = generateNewClaimStepWhereWhenFormSchema();
  }

  return stepWhereWhenFormSchema;
}

export { generateNewClaimStepWhereWhenFormSchema, getNewClaimStepWhereWhenFormSchema };

export type { GenerateNewClaimStepWhereWhenFormSchemaParams };
